import { useState, useEffect, useCallback, useMemo } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Plus, Search, X, Trash2, Mail, Phone, ExternalLink, MapPin,
  DollarSign, ShoppingCart, Truck, Briefcase, AlertCircle, Check,
  ChevronRight, Edit2, UserPlus, Globe
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const CATEGORIES = [
  { key: "haircare",  label: "Haircare" },
  { key: "skincare",  label: "Skincare" },
  { key: "nailcare",  label: "Nail Care" },
  { key: "tools",     label: "Tools & Equipment" },
  { key: "retail",    label: "Retail / Boutique" },
  { key: "cleaning",  label: "Cleaning / Sanitation" },
  { key: "supplies",  label: "General Supplies" },
  { key: "other",     label: "Other" },
];

const fmtMoney = (n) => `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
};

// ─── Editor Drawer ─────────────────────────────────────────────
function VendorEditor({ vendor, adHocName, onClose, onSaved, onDeleted }) {
  // `adHocName` is non-null when the user is promoting an ad-hoc vendor.
  const isNew = !vendor?.vendor_id;
  const [form, setForm] = useState(() => ({
    name: vendor?.name || adHocName || "",
    category: vendor?.category || "haircare",
    address: vendor?.address || "",
    website: vendor?.website || "",
    account_number: vendor?.account_number || "",
    payment_terms: vendor?.payment_terms || "Net 30",
    notes: vendor?.notes || "",
    status: vendor?.status || "active",
    contacts: vendor?.contacts || [],
  }));
  const [saving, setSaving] = useState(false);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const updateContact = (idx, k, v) => {
    const next = [...form.contacts];
    next[idx] = { ...next[idx], [k]: v };
    setForm(f => ({ ...f, contacts: next }));
  };
  const addContactRow = () =>
    setForm(f => ({ ...f, contacts: [...f.contacts, { name: "", role: "", email: "", phone: "" }] }));
  const removeContactRow = (idx) =>
    setForm(f => ({ ...f, contacts: f.contacts.filter((_, i) => i !== idx) }));

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Vendor name is required"); return; }
    setSaving(true);
    try {
      if (isNew) {
        const res = await api.post("/vendors", form);
        toast.success(adHocName ? `${form.name} registered` : "Vendor added");
        onSaved(res.data);
      } else {
        const res = await api.put(`/vendors/${vendor.vendor_id}`, form);
        toast.success("Vendor updated");
        onSaved(res.data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const del = async () => {
    if (!vendor?.vendor_id) return;
    if (!window.confirm(`Remove vendor "${vendor.name}"? Products & purchase orders are preserved (the link is just unset).`)) return;
    try {
      await api.delete(`/vendors/${vendor.vendor_id}`);
      toast.success("Vendor removed");
      onDeleted?.(vendor.vendor_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/30 backdrop-blur-sm" data-testid="vendor-editor"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-[560px] h-full bg-white overflow-y-auto animate-slideInRight shadow-2xl">
        <div className="sticky top-0 bg-white z-10 border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 font-['Manrope']">
              {isNew ? (adHocName ? "Register vendor" : "New vendor") : "Edit vendor"}
            </p>
            <h2 className="font-['Outfit'] text-xl font-semibold text-slate-900 truncate" data-testid="vendor-editor-title">
              {form.name || "Untitled vendor"}
            </h2>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0 ml-3">
            {!isNew && (
              <button onClick={del} data-testid="delete-vendor"
                className="w-9 h-9 rounded-full hover:bg-rose-50 flex items-center justify-center">
                <Trash2 className="w-4 h-4 text-rose-600" />
              </button>
            )}
            <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center">
              <X className="w-4 h-4 text-slate-600" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {adHocName && isNew && (
            <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
              <span className="font-semibold">Promoting ad-hoc vendor.</span> &quot;{adHocName}&quot; already appears on products/POs. Saving will register it so contacts and spend tracking work.
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Vendor Name *</p>
              <Input value={form.name} onChange={e => setField("name", e.target.value)}
                placeholder="L'Oreal Professionnel" data-testid="vendor-name"
                className="rounded-xl border-slate-200" />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Category</p>
              <select value={form.category} onChange={e => setField("category", e.target.value)}
                data-testid="vendor-category"
                className="w-full h-10 px-3 rounded-xl border border-slate-200 text-sm text-slate-700 focus:outline-none focus:border-rose-300">
                {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Account Number</p>
              <Input value={form.account_number} onChange={e => setField("account_number", e.target.value)}
                placeholder="ACCT-12345" data-testid="vendor-account"
                className="rounded-xl border-slate-200" />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Payment Terms</p>
              <Input value={form.payment_terms} onChange={e => setField("payment_terms", e.target.value)}
                placeholder="Net 30 / COD / etc." data-testid="vendor-terms"
                className="rounded-xl border-slate-200" />
            </div>
            <div className="md:col-span-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Website</p>
              <Input value={form.website} onChange={e => setField("website", e.target.value)}
                placeholder="https://…" data-testid="vendor-website"
                className="rounded-xl border-slate-200" />
            </div>
            <div className="md:col-span-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Address</p>
              <Input value={form.address} onChange={e => setField("address", e.target.value)}
                placeholder="Street, city, state" data-testid="vendor-address"
                className="rounded-xl border-slate-200" />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Status</p>
              <select value={form.status} onChange={e => setField("status", e.target.value)}
                data-testid="vendor-status"
                className="w-full h-10 px-3 rounded-xl border border-slate-200 text-sm text-slate-700 focus:outline-none focus:border-rose-300">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          {/* Contacts */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Contacts ({form.contacts.length})</p>
              <button onClick={addContactRow} data-testid="add-contact-row"
                className="text-xs inline-flex items-center gap-1 px-2.5 h-7 rounded-full bg-slate-900 text-white hover:bg-slate-800">
                <UserPlus className="w-3 h-3" /> Add contact
              </button>
            </div>
            <div className="space-y-2">
              {form.contacts.length === 0 && (
                <p className="text-xs text-slate-400 italic">No contacts yet. Add a rep, education manager, or billing contact.</p>
              )}
              {form.contacts.map((c, idx) => (
                <div key={idx} className="bg-[#FAFAFA] rounded-xl p-3 space-y-2" data-testid={`contact-row-${idx}`}>
                  <div className="grid grid-cols-12 gap-2">
                    <Input value={c.name || ""} onChange={e => updateContact(idx, "name", e.target.value)}
                      placeholder="Name" className="col-span-7 h-9 rounded-lg border-slate-200 text-sm" />
                    <Input value={c.role || ""} onChange={e => updateContact(idx, "role", e.target.value)}
                      placeholder="Role (Rep, Billing…)" className="col-span-4 h-9 rounded-lg border-slate-200 text-sm" />
                    <button onClick={() => removeContactRow(idx)} data-testid={`remove-contact-${idx}`}
                      className="col-span-1 rounded-lg hover:bg-rose-50 flex items-center justify-center">
                      <Trash2 className="w-4 h-4 text-rose-500" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={c.email || ""} onChange={e => updateContact(idx, "email", e.target.value)}
                      placeholder="email@…" className="h-9 rounded-lg border-slate-200 text-sm" />
                    <Input value={c.phone || ""} onChange={e => updateContact(idx, "phone", e.target.value)}
                      placeholder="Phone" className="h-9 rounded-lg border-slate-200 text-sm" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Notes</p>
            <textarea value={form.notes} onChange={e => setField("notes", e.target.value.slice(0, 2000))}
              rows={3} data-testid="vendor-notes" placeholder="Lead times, minimums, account quirks, education credits…"
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-rose-300 resize-none" />
          </div>

          <div className="sticky bottom-0 -mx-6 bg-white border-t border-slate-100 px-6 py-4 flex items-center gap-3">
            <button onClick={onClose} className="text-sm text-slate-500 hover:text-slate-900 px-3 h-10">
              Cancel
            </button>
            <div className="flex-1" />
            <Button onClick={save} disabled={saving} data-testid="save-vendor"
              className="bg-slate-900 text-white rounded-full h-10 px-6 hover:bg-slate-800 disabled:opacity-50">
              <Check className="w-4 h-4 mr-1.5" />
              {saving ? "Saving…" : (isNew ? "Save vendor" : "Save changes")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Vendors Tab ───────────────────────────────────────────────
export default function VendorsTab() {
  const [vendors, setVendors] = useState([]);
  const [spend, setSpend] = useState({ rows: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | active | inactive | ad_hoc
  const [editor, setEditor] = useState(null); // null | vendor | { adHocName }

  const fetchAll = useCallback(async () => {
    try {
      const [vRes, sRes] = await Promise.all([
        api.get("/vendors"),
        api.get("/vendors/spend"),
      ]);
      setVendors(vRes.data || []);
      setSpend(sRes.data || { rows: [], totals: {} });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load vendors");
    }
    setLoading(false);
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Merge spend rows with full vendor records. The spend rollup has the
  // computed numbers + registered/ad_hoc flag; we fold the registered
  // vendor record back in for contact counts.
  const merged = useMemo(() => {
    const byVendorId = new Map(vendors.map(v => [v.vendor_id, v]));
    return (spend.rows || []).map(row => {
      const v = row.vendor_id ? byVendorId.get(row.vendor_id) : null;
      return {
        ...row,
        contacts: v?.contacts || [],
        category_label: CATEGORIES.find(c => c.key === row.category)?.label || row.category || "—",
        website: v?.website,
        full: v,
      };
    });
  }, [vendors, spend]);

  const filtered = useMemo(() => {
    let arr = merged;
    if (filter === "active") arr = arr.filter(r => r.registered && r.status === "active");
    else if (filter === "inactive") arr = arr.filter(r => r.registered && r.status === "inactive");
    else if (filter === "ad_hoc") arr = arr.filter(r => !r.registered);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      arr = arr.filter(r => (r.name || "").toLowerCase().includes(q)
        || (r.contacts || []).some(c => (c.name || "").toLowerCase().includes(q) || (c.email || "").toLowerCase().includes(q)));
    }
    return arr;
  }, [merged, filter, search]);

  const totals = spend.totals || {};

  const onSaved = (saved) => {
    if (!saved) return;
    setVendors(prev => {
      const idx = prev.findIndex(v => v.vendor_id === saved.vendor_id);
      if (idx >= 0) { const next = [...prev]; next[idx] = saved; return next; }
      return [...prev, saved];
    });
    setEditor(saved);
    fetchAll();
  };
  const onDeleted = () => {
    setEditor(null);
    fetchAll();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="vendors-tab">
      {/* Stats strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { Icon: Truck,        label: "Vendors",         value: totals.vendor_count || 0, sub: `${totals.ad_hoc_count || 0} ad-hoc`, color: "#F43F5E" },
          { Icon: DollarSign,   label: "Total Spend",     value: fmtMoney(totals.total_spend), sub: "Received POs", color: "#10B981" },
          { Icon: ShoppingCart, label: "Committed",       value: fmtMoney(totals.committed_spend), sub: "Sent, not received", color: "#F59E0B" },
          { Icon: Briefcase,    label: "Total Orders",    value: totals.total_orders || 0, sub: "All-time POs", color: "#8B5CF6" },
        ].map((s) => (
          <div key={s.label} className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`vendor-stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
            <s.Icon className="w-5 h-5 mb-2" style={{ color: s.color }} strokeWidth={1.5} />
            <p className="font-['Outfit'] text-2xl font-medium text-slate-900">{s.value}</p>
            <p className="text-xs text-slate-400 mt-1 uppercase tracking-wider font-semibold font-['Manrope']">{s.label}</p>
            {s.sub && <p className="text-[11px] text-slate-400 mt-0.5">{s.sub}</p>}
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4 mb-4">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          <div className="relative flex-1 min-w-0">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              data-testid="vendor-search" placeholder="Search vendor, contact name, or email…"
              className="w-full h-10 pl-10 pr-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-rose-300" />
          </div>
          <div className="flex items-center gap-2">
            {[
              { key: "all", label: "All" },
              { key: "active", label: "Active" },
              { key: "inactive", label: "Inactive" },
              { key: "ad_hoc", label: "Ad-hoc" },
            ].map(p => (
              <button key={p.key} onClick={() => setFilter(p.key)} data-testid={`vendor-filter-${p.key}`}
                className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${
                  filter === p.key ? "bg-slate-900 text-white" : "bg-[#F3F3F3] text-slate-600 hover:bg-[#E6E4E0]"
                }`}>
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Vendor list */}
      {filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
          <Truck className="w-10 h-10 text-slate-200 mx-auto mb-3" />
          <p className="text-sm text-slate-500">No vendors match these filters</p>
          {filter === "all" && vendors.length === 0 && (
            <button onClick={() => setEditor({ adHocName: null })} className="text-xs text-rose-600 hover:underline mt-3 font-medium">
              Add your first vendor
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="vendors-table">
          <table className="w-full">
            <thead>
              <tr className="bg-[#FAFAFA] border-b border-slate-200">
                <th className="text-left px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Vendor</th>
                <th className="text-left px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Category</th>
                <th className="text-left px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Contacts</th>
                <th className="text-right px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Products</th>
                <th className="text-right px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Orders</th>
                <th className="text-right px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Total Spend</th>
                <th className="text-right px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Last Order</th>
                <th className="w-12 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(row => {
                const primary = (row.contacts || [])[0];
                return (
                  <tr key={row.vendor_id || `adhoc-${row.name}`}
                    className="border-b border-slate-200 hover:bg-[#FAFAFA] transition-colors cursor-pointer"
                    data-testid={`vendor-row-${row.vendor_id || row.name}`}
                    onClick={() => setEditor(row.registered ? row.full : { adHocName: row.name })}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-slate-900">{row.name}</p>
                        {!row.registered && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 font-medium">Ad-hoc</span>
                        )}
                        {row.registered && row.status === "inactive" && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 font-medium">Inactive</span>
                        )}
                      </div>
                      {row.website && (
                        <a href={row.website} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                          className="text-[11px] text-rose-500 hover:underline inline-flex items-center gap-0.5 mt-0.5">
                          <Globe className="w-2.5 h-2.5" /> {row.website.replace(/^https?:\/\//, "").slice(0, 30)}
                        </a>
                      )}
                    </td>
                    <td className="px-3 py-4 text-xs text-slate-600">{row.category_label}</td>
                    <td className="px-3 py-4">
                      {primary ? (
                        <div className="text-xs">
                          <p className="text-slate-700 font-medium">{primary.name}{primary.role ? ` · ${primary.role}` : ""}</p>
                          <div className="flex items-center gap-2 text-slate-400 mt-0.5">
                            {primary.email && <span className="inline-flex items-center gap-0.5 truncate max-w-[150px]"><Mail className="w-2.5 h-2.5" /> {primary.email}</span>}
                            {primary.phone && <span className="inline-flex items-center gap-0.5"><Phone className="w-2.5 h-2.5" /> {primary.phone}</span>}
                          </div>
                          {(row.contacts.length > 1) && (
                            <p className="text-[10px] text-slate-400 mt-0.5">+{row.contacts.length - 1} more</p>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400 italic">No contacts</span>
                      )}
                    </td>
                    <td className="px-3 py-4 text-sm text-right text-slate-700">{row.product_count}</td>
                    <td className="px-3 py-4 text-sm text-right text-slate-700">{row.order_count}</td>
                    <td className="px-3 py-4 text-sm text-right">
                      <p className="font-medium text-slate-900">{fmtMoney(row.total_spend)}</p>
                      {row.committed_spend > 0 && (
                        <p className="text-[10px] text-amber-600">+{fmtMoney(row.committed_spend)} committed</p>
                      )}
                    </td>
                    <td className="px-3 py-4 text-xs text-right text-slate-400">{fmtDate(row.last_order_date)}</td>
                    <td className="px-3 py-4 text-right">
                      <ChevronRight className="w-4 h-4 text-slate-300 inline" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editor && (
        <VendorEditor
          vendor={editor.vendor_id ? editor : null}
          adHocName={editor.adHocName || null}
          onClose={() => setEditor(null)}
          onSaved={onSaved}
          onDeleted={onDeleted}
        />
      )}

      {/* Floating Add button */}
      <button onClick={() => setEditor({ adHocName: null })} data-testid="add-vendor-btn"
        title="Add vendor"
        className="fixed bottom-24 right-6 z-30 w-14 h-14 rounded-full bg-slate-900 text-white shadow-lg shadow-slate-900/20 hover:bg-rose-500 hover:shadow-rose-500/30 transition-all flex items-center justify-center">
        <Plus className="w-5 h-5" strokeWidth={2} />
      </button>
    </div>
  );
}
